/* 210 §1 — A LIST THAT COULD NOT LOAD SAYS SO.

   What prod showed (25/09): in Safari, Dossiers read 0 and the dashboard's
   time-entry picker offered one collective case and no case — while the
   database held its 40 cases, the server answered every `GET /api/cases` 200
   or 304, and a reload brought them back. The boot's `_apiOr` (App.tsx)
   swallowed any failure, or an answer that was not an array, and answered the
   local copy `ld` read, or `[]` — no message, and nothing asked for the list
   again until a reload.

   CENSUS (210 §1, measured at 5e8ab621) — who still wrote those local copies.
   Nothing, for 16 of the 18 lists the boot loads: `persist` writes its copy
   only for a key with no entity API (App.tsx `_apiEnt`), and every list but
   the invoices has one — while no persist call names the invoices at all. The
   agenda's refresh wrote `lx:ag` (AgendaView.tsx:292), and a pre-rename
   migration rewrote an old `lx:ca` and set it IN PLACE of the list the server
   had just answered (App.tsx:5001-5019). Nothing else reads a copy: the
   fallback could only answer `[]`, or a copy left from long ago. The fallback,
   the agenda's write and the migration go together.

   THE RULE [ADVISOR]: a list that fails is asked again, twice, a second apart,
   and each failure goes to the console — which list, the status or the error.
   Still failing, NOTHING takes its place: the list is marked as not loaded,
   and every screen that shows it, counts it or picks from it says « La liste
   n’a pas pu être chargée » with « Réessayer », which asks for that list
   alone — never « 0 », never an empty list. A list not loaded is asked again
   when the window comes back (App's focus cycle).

   This file is the policy — no React in the asking, so a recording runs it as
   it is (recordings/210-list-load) — and the context the screens read
   (`ListLoadContext`, provided by App). `ListNotLoaded` (SharedUI.tsx) draws
   the words. */
import { createContext, useContext } from "react";
// A function type's named parameters are unused variables to the base
// `no-unused-vars` (emailPdfLink.ts says so): the two below reuse declared
// types — React's `Dispatch<A>` is `(value: A) => void` — and name none.
import type { Dispatch } from "react";

/** What a failed ask leaves: the HTTP status when the server answered, else the error's words. */
export interface ListFailure {
  status: number | null;
  error: string;
}
export type ListAnswer = { ok: true; rows: unknown[] } | { ok: false; failure: ListFailure };
/** One list: its key (the `SK` key the rest of the app knows it by), its name for the console, its request. */
export interface ListSource {
  key: string;
  name: string;
  fetch: () => Promise<unknown>;
}

/** The first ask and the two after it. */
export const LIST_ASKS = 3;
/** A second apart. */
export const LIST_GAP_MS = 1000;

type Log = Dispatch<string>;
const warn: Log = (line) => {
  globalThis.console.warn(line);
};
const pause = (ms: number) =>
  new Promise<void>((r) => {
    globalThis.setTimeout(r, ms);
  });

/** The console's line for one failed ask: which list, which ask, the status or the error. */
export function listFailureLine(name: string, ask: number, asks: number, f: ListFailure): string {
  const why = f.status == null ? f.error : "HTTP " + String(f.status);
  return "[lists] " + name + " could not be loaded (ask " + String(ask) + " of " + String(asks) + "): " + why;
}

/** Ask once. An answer that is not an array is a failure too — it is what `_apiOr` read as « no list » and replaced. */
export async function askList(src: ListSource, ask: number, asks: number, log: Log = warn): Promise<ListAnswer> {
  let failure: ListFailure;
  try {
    const r = await src.fetch();
    if (Array.isArray(r)) return { ok: true, rows: r };
    failure = { status: null, error: `the answer is not a list (${r === null ? "null" : typeof r})` };
  } catch (e) {
    const st = e && typeof e === "object" ? (e as { status?: unknown }).status : undefined;
    failure = {
      status: typeof st === "number" ? st : null,
      error: e instanceof Error ? `${e.name}: ${e.message}` : String(e),
    };
  }
  log(listFailureLine(src.name, ask, asks, failure));
  return { ok: false, failure };
}

/** Ask each list in order, then the failed ones again, a second apart, until `asks` asks in all. `from` is the
 *  number of the first ask made here: the boot asks each list once as it always did, and hands its failures
 *  over from the second. The answer per key is its LAST ask's. */
export async function loadLists(
  sources: readonly ListSource[],
  opts: { from?: number; asks?: number; gapMs?: number; sleep?: typeof pause; log?: Log } = {},
): Promise<Map<string, ListAnswer>> {
  const { from = 1, asks = LIST_ASKS, gapMs = LIST_GAP_MS, sleep = pause, log = warn } = opts;
  const out = new Map<string, ListAnswer>();
  let pending = [...sources];
  for (let ask = from; ask <= asks && pending.length; ask++) {
    if (ask > 1) await sleep(gapMs);
    const still: ListSource[] = [];
    for (const s of pending) {
      const a = await askList(s, ask, asks, log);
      out.set(s.key, a);
      if (!a.ok) still.push(s);
    }
    pending = still;
  }
  return out;
}

/** What the screens read: the lists not loaded (by key), the ones being asked again right now, and the ask. */
export interface ListLoadState {
  notLoaded: Readonly<Record<string, ListFailure>>;
  asking: ReadonlySet<string>;
  retry: Dispatch<readonly string[]>;
}
/** No provider (a harness, a recording) — every list loaded, nothing to ask. */
const askNothing: Dispatch<readonly string[]> = () => undefined;
export const ListLoadContext = createContext<ListLoadState>({
  notLoaded: {},
  asking: new Set<string>(),
  retry: askNothing,
});

/** Of the keys named, those whose list is not loaded — empty when every one of them is. */
export function useListsNotLoaded(keys: readonly string[]): string[] {
  const { notLoaded } = useContext(ListLoadContext);
  return keys.filter((k) => Boolean(notLoaded[k]));
}

/* 211 §10 — A MATTER PAGE'S OWN LISTS, THE SAME RULE (docs/211-deploy-note.md
   §8.5, the census). Five reads a matter page makes itself answered `[]` when
   they failed: the two chronicles, a collective case's mailings, the documents
   section's templates, the e-mail pane's parties. Each is asked
   by `usePageList` (src/hooks/usePageList.ts) through `loadLists` above, and
   its host layers it over App's marks with `layerPageLists` — so
   `ListNotLoaded` reads the one context it always read, a boot list inside the
   same host keeps App's mark, and « Réessayer » asks each list's own owner. */
/** The page lists' keys — never an `SK` key, so a layer never hides a boot list's mark. */
export const PAGE_LISTS = {
  chronicle: "page:chronicle",
  mailings: "page:mailings",
  docTemplates: "page:docTemplates",
  emailParties: "page:emailParties",
  // 212 §22 — the windows' and the sections' own reads (src/hooks/usePageList.ts `useReadMark`)
  subtasks: "page:subtasks",
  docTracking: "page:docTracking",
  bulkTemplates: "page:bulkTemplates",
  bulkHistory: "page:bulkHistory",
  attachmentFolders: "page:attachmentFolders",
  invoiceSources: "page:invoiceSources",
  cdd: "page:cdd",
  calculator: "page:calculator",
  m365Status: "page:m365Status",
  // 213 §1, §4 — « Mes connexions »' own read and the administrator's shortcut links (src/components/DoorKeysPanel.tsx)
  doorKeys: "page:doorKeys",
  shortcutLinks: "page:shortcutLinks",
} as const;
/** One page list as its host holds it: what its last ask left, whether it is being asked, how to ask it again. */
export interface PageListEntry {
  key: string;
  failure: ListFailure | null;
  asking: boolean;
  retry: () => void;
}
/** App's marks with the host's own lists over them. « Réessayer » on a page list asks its owner; on any other, App. */
export function layerPageLists(outer: ListLoadState, entries: readonly (PageListEntry | null | undefined)[]): ListLoadState {
  const mine = entries.filter((e): e is PageListEntry => Boolean(e));
  if (!mine.length) return outer;
  const notLoaded: Record<string, ListFailure> = { ...outer.notLoaded };
  const asking = new Set(outer.asking);
  for (const e of mine) {
    if (e.failure) notLoaded[e.key] = e.failure;
    if (e.asking) asking.add(e.key);
  }
  const retry: Dispatch<readonly string[]> = (keys) => {
    const rest: string[] = [];
    for (const k of keys) {
      const own = mine.find((e) => e.key === k);
      if (own) own.retry();
      else rest.push(k);
    }
    if (rest.length) outer.retry(rest);
  };
  return { notLoaded, asking, retry };
}
